import { createTheme, alpha } from '@mui/material/styles';
import componentsOverride from './idotoo/overrides/index';

// ============================================
// UNIFIED THEME CONFIGURATION
// ============================================
// This is the single source of truth for all theme settings
// Edit values here to update the entire app's appearance

// Brand Colors - Softer Pink Palette
export const colors = {
  primary: {
    main: '#e7548b',      // Main brand color
    light: '#f4a8c4',     // Light variant
    dark: '#d43d77',      // Dark variant
    contrastText: '#fff',
  },
  secondary: {
    main: '#fce4ec',      // Light pink background
    light: '#fff0f5',     // Very light pink
    dark: '#f4a8c4',      // Medium variant
    contrastText: '#000',
  },
  success: {
    main: '#4CAF50',
    light: '#81C784',
    dark: '#388E3C',
  },
  warning: {
    main: '#FF9800',
    light: '#FFB74D',
    dark: '#F57C00',
  },
  error: {
    main: '#f44336',
    light: '#e57373',
    dark: '#d32f2f',
  },
  grey: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    offWhite: '#fafafa',
  },
  text: {
    primary: '#000000',
    secondary: '#7F8C8D',
  },
};

// Typography Configuration
export const typography = {
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontSize: 16,
  h1: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 400,
    fontSize: '2.8rem',
    lineHeight: 1.2,
  },
  h2: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 400,
    fontSize: '2rem',
    lineHeight: 1.3,
  },
  h3: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 400,
    fontSize: '1.75rem',
    lineHeight: 1.4,
  },
  h4: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 400,
    fontSize: '1.5rem',
    lineHeight: 1.4,
  },
  h5: {
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 400,
    fontSize: '1.25rem',
    lineHeight: 1.5,
  },
  h6: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 400,
    fontSize: '0.9375rem', // 15px - slightly reduced from 1rem (16px)
    lineHeight: 1.5,
  },
  body1: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  body2: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '0.875rem',
    lineHeight: 1.57,
  },
  caption: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    lineHeight: 1.66,
  },
  overline: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    lineHeight: 2.66,
  },
  button: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '0.875rem',
  },
  subtitle1: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '1rem',
    lineHeight: 1.5,
  },
  subtitle2: {
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: '0.875rem',
    lineHeight: 1.57,
  },
};

// Spacing & Layout
export const spacing = 8; // Base spacing unit in pixels

export const layout = {
  drawerWidth: 280,
  containerMaxWidth: 1200,
  borderRadius: 16, // Maximum border radius limit
};

// Shadows (softer shadows for modern look)
export const shadows = [
  'none',
  '0px 1px 2px rgba(0,0,0,0.05)',
  '0px 2px 4px rgba(0,0,0,0.06)',
  '0px 3px 6px rgba(0,0,0,0.07)',
  '0px 4px 8px rgba(0,0,0,0.08)',
  '0px 5px 10px rgba(0,0,0,0.09)',
  '0px 6px 12px rgba(0,0,0,0.10)',
  '0px 7px 14px rgba(0,0,0,0.11)',
  '0px 8px 16px rgba(0,0,0,0.12)',
  '0px 9px 18px rgba(0,0,0,0.13)',
  '0px 10px 20px rgba(0,0,0,0.14)',
  '0px 11px 22px rgba(0,0,0,0.15)',
  '0px 12px 24px rgba(0,0,0,0.16)',
  '0px 13px 26px rgba(0,0,0,0.17)',
  '0px 14px 28px rgba(0,0,0,0.18)',
  '0px 15px 30px rgba(0,0,0,0.19)',
  '0px 16px 32px rgba(0,0,0,0.20)',
  '0px 17px 34px rgba(0,0,0,0.21)',
  '0px 18px 36px rgba(0,0,0,0.22)',
  '0px 19px 38px rgba(0,0,0,0.23)',
  '0px 20px 40px rgba(0,0,0,0.24)',
  '0px 21px 42px rgba(0,0,0,0.25)',
  '0px 22px 44px rgba(0,0,0,0.26)',
  '0px 23px 46px rgba(0,0,0,0.27)',
  '0px 24px 48px rgba(0,0,0,0.28)',
];

// Custom shadows for specific components
export const customShadows = {
  card: '0px 2px 4px rgba(0,0,0,0.06)',
  z8: '0px 8px 16px rgba(0,0,0,0.12)',
  z16: '0px 16px 32px rgba(0,0,0,0.20)',
  z24: '0px 24px 48px rgba(0,0,0,0.28)',
};

// Gradient Presets
export const gradients = {
  primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  secondary: 'linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.6) 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
  light: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%)',
};

/** Shared styles for route-level page headings (`h2.pagename`). */
export const pagenameTextStyles = {
  margin: 0,
  marginBottom: '0.35em',
  fontFamily: '"Plus Jakarta Sans", sans-serif',
  fontWeight: 700,
  fontSize: '1.5rem',
  lineHeight: 1.4,
  color: colors.text.primary,
};

// Component Style Presets
export const componentPresets = {
  // Card Variants
  card: {
    default: {
      borderRadius: Math.min(layout.borderRadius, 16),
      boxShadow: 'none',
      backgroundColor: 'rgba(255, 255, 255, 0.5)', // 50% transparent white
      border: '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(10px)', // Glassmorphism effect
      '&:hover': {
        borderColor: 'rgba(0, 0, 0, 0.12)',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
      },
    },
    elevated: {
      borderRadius: Math.min(layout.borderRadius, 16),
      boxShadow: shadows[2],
      transition: 'all 0.3s ease',
      '&:hover': {
        boxShadow: shadows[4],
      },
    },
    interactive: {
      borderRadius: Math.min(layout.borderRadius, 16),
      boxShadow: 'none',
      border: '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.2s ease',
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: shadows[3],
        borderColor: colors.primary.main,
      },
    },
  },
  
  // Button Variants
  button: {
    primary: {
      borderRadius: Math.min(layout.borderRadius, 16),
      padding: '8px 24px',
      boxShadow: 'none',
      backgroundColor: '#000000',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: '#333333',
        boxShadow: shadows[2],
      },
    },
    secondary: {
      borderRadius: Math.min(layout.borderRadius, 16),
      padding: '8px 20px',
      borderWidth: '1.5px',
      borderColor: '#000000',
      color: '#000000',
      '&:hover': {
        borderWidth: '1.5px',
        borderColor: '#000000',
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
      },
    },
  },
  
  // Container Variants
  container: {
    page: {
      padding: 3,
      maxWidth: layout.containerMaxWidth,
      margin: '0 auto',
      width: '100%',
    },
    section: {
      marginBottom: 4,
    },
  },
  
  // Text Variants
  text: {
    pagename: pagenameTextStyles,
    pageTitle: pagenameTextStyles,
    sectionTitle: {
      fontWeight: 500,
      marginBottom: 2,
      color: colors.text.primary,
    },
    sectionDescription: {
      color: colors.text.secondary,
      marginBottom: 3,
    },
  },
};

// Create the unified theme
const unifiedTheme = createTheme({
  palette: colors,
  typography,
  spacing,
  shape: {
    borderRadius: layout.borderRadius,
  },
  // Above modal (1300) and any deep stacking; must pair with PortalSnackbar for Dialog contexts
  zIndex: {
    snackbar: 20000,
  },
  shadows,
  customShadows,
  gradients,
  components: {
    // Global component overrides
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          padding: 0,
          backgroundColor: 'transparent', // Transparent for gradient background
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          ...componentPresets.card.default,
          borderRadius: '16px !important',
          '& .MuiCard-root': {
            borderRadius: '16px !important',
          },
          '& .MuiCardContent-root': {
            borderRadius: '16px !important',
            // Nested cards inside CardContent should have solid white background
            '& .MuiCard-root': {
              backgroundColor: '#ffffff',
              backdropFilter: 'none',
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: layout.borderRadius,
          textTransform: 'none',
          fontWeight: 500,
        },
        // Primary contained buttons: solid black background with white text
        containedPrimary: {
          backgroundColor: '#000000',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#333333',
            boxShadow: shadows[2],
          },
        },
        // Secondary outlined buttons: black outline with black text
        outlinedSecondary: {
          borderColor: '#000000',
          borderWidth: '1.5px',
          color: '#000000',
          '&:hover': {
            borderColor: '#000000',
            borderWidth: '1.5px',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        },
        contained: componentPresets.button.primary,
        outlined: componentPresets.button.secondary,
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: layout.borderRadius,
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: layout.borderRadius,
        },
        // Exception for drawer papers - no border radius
        elevation0: {
          '&.MuiDrawer-paper': {
            borderRadius: '0px !important',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.secondary.light,
          color: colors.text.primary,
          boxShadow: 'none',
          borderBottom: `1px solid ${colors.grey[200]}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: 'transparent', // No background
          borderRight: '1px solid rgba(0, 0, 0, 0.08)',
          width: layout.drawerWidth,
          borderRadius: '0px !important',
          boxShadow: 'none !important',
        },
        docked: {
          '& .MuiDrawer-paper': {
            borderRadius: '0px !important',
            boxShadow: 'none !important',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderRadius: layout.borderRadius,
          margin: '4px 8px',
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.08),
          },
          '&.Mui-selected': {
            backgroundColor: colors.secondary.dark,
            '&:hover': {
              backgroundColor: colors.secondary.dark,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '30px',
          height: 'auto',
          padding: '1px 0px',
          fontSize: '10px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: layout.borderRadius,
        },
      },
    },
  },
});

// Apply component overrides from the organized override files
// This allows global styling changes by editing individual override files
// Override files take precedence over inline component definitions
unifiedTheme.components = {
  ...unifiedTheme.components,
  ...componentsOverride(unifiedTheme),
};

// Chat-specific theme extensions
export { chatTheme, chatAnimations } from './chatTheme';

// Export helper to access presets in components
export const useThemePresets = () => componentPresets;

export default unifiedTheme;